import React, { useState, useEffect } from 'react';
import { get } from '@/features/shared/lib/api';

/**
 * CitingPapersList
 * - Component for displaying citing papers from Scopus API
 * - Props: query (string) - Scopus query parameter (e.g., "REF(2-s2.0-85180009194)")
 */
export default function CitingPapersList({ query, className = '' }) {
  const [data, setData] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Scopus API 호출
  useEffect(() => {
    if (!query) {
      setData([]);
      return;
    }

    const fetchScopusData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await get('/rm/v1/scopus/search', {
          params: {
            query: query,
            start: 0,
            count: 5,
          },
        });

        // API 응답 구조 확인 (status, result)
        if (!response || !response.status || !response.result) {
          setData([]);
          return;
        }

        const result = response.result;
        const searchResults = result['search-results'] || {};
        const total = parseInt(searchResults['opensearch:totalResults'] || '0');
        setTotalResults(total);

        // Entry 데이터 파싱
        const entries = searchResults.entry || [];
        const parsedData = entries.map((entry) => {
          // 날짜 포맷팅 (2025-02-01 -> 2025.02)
          const formatDate = (dateStr) => {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            return `${year}.${month}`;
          };

          // 볼륨 및 이슈 포맷팅
          const volume = entry['prism:volume'] || '';
          const issue = entry['prism:issueIdentifier'] || '';
          const volumeText = volume ? (issue ? `Vol ${volume}(${issue})` : `Vol ${volume}`) : '';

          // 페이지 범위 포맷팅
          const pageRange = entry['prism:pageRange'] || '';
          const pagesText = pageRange ? `pp.${pageRange}` : '';

          return {
            eid: entry.eid || '',
            title: entry['dc:title'] || '',
            publicationName: entry['prism:publicationName'] || '',
            volume: volumeText,
            pages: pagesText,
            date: formatDate(entry['prism:coverDate']),
          };
        });

        setData(parsedData);
      } catch (err) {
        console.error('Error fetching Scopus data:', err);
        setData([]);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchScopusData();
  }, [query]);

  if (!query) {
    return null;
  }

  if (loading) {
    return (
      <div className={className}>
        <div className="text-center py-8 text-[#6C7687]">Loading...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <ul className="flex flex-col px-[32px] border border-[#E4E8F1] rounded-[12px]">
        {data.map((paper, index) => (
          <li
            key={paper.eid || index}
            className={`py-[32px] ${index < data.length - 1 ? 'border-b border-b-[#E4E8F1]' : ''}`}
          >
            <h3 className="mb-4 text-[16px] font-bold leading-[150%]">
              {paper.eid ? (
                <a
                  href={`https://www.scopus.com/record/display.uri?eid=${paper.eid}&origin=inward`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline underline-offset-2 decoration-1"
                >
                  {paper.title}
                </a>
              ) : (
                paper.title
              )}
            </h3>

            <div className="flex flex-wrap gap-x-2 gap-y-3 items-center text-[14px] text-[#6C7687] font-semibold leading-[100%]">
              <p className="text-[#B4BCCC] font-medium mr-[5px]">Source</p>
              <span>{paper.publicationName}</span>
              {paper.volume && (
                <>
                  <span className="w-[1px] h-[10px] bg-[#B4BCCC] inline-block"></span>
                  <span>{paper.volume}</span>
                </>
              )}
              {paper.pages && (
                <>
                  <span className="w-[1px] h-[10px] bg-[#B4BCCC] inline-block"></span>
                  <span>{paper.pages}</span>
                </>
              )}
              {paper.date && (
                <>
                  <span className="w-[1px] h-[10px] bg-[#B4BCCC] inline-block"></span>
                  <span>{paper.date}</span>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>

      {totalResults > 5 && (
        <div className="mt-4 flex justify-center">
          <a
            href={`https://www.scopus.com/results/results.uri?st1=&s=${encodeURIComponent(query)}&limit=10&origin=searchbasic`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 bg-[#F3F5F9] border border-[#E4E8F1] rounded-[12px] text-[14px] text-[#6C7687] font-semibold hover:bg-[#EEF1F8] transition-colors cursor-pointer"
          >
            <span>More (+{totalResults - 5})</span>
            <img
              src={`${rmVars.themeUrl}images/expand_more_333.png`}
              className="w-4 h-4 transition-transform duration-200"
              style={{ filter: 'invert(48%) sepia(8%) saturate(675%) hue-rotate(182deg) brightness(93%) contrast(89%)' }}
              alt="expand"
            />
          </a>
        </div>
      )}
    </div>
  );
}

